import { ReactNode } from 'react';
import styled from 'styled-components';

import { Button } from './Button';
import { IconArrowLeft } from './icons';

/**
 * Обёртка для карточек претензий/исков и договоров, рендерящихся как
 * полноценные страницы (а не модалки). Решает три задачи:
 *   1. Заголовок «Карточка претензии / иска» с кнопкой «Назад» сверху;
 *   2. Ограничение ширины содержимого (max-width 1100px) и центровка —
 *      на широких экранах текст не растекается на всю ширину, на узких
 *      использует полную ширину viewport'а;
 *   3. Воздух вокруг — карточка не липнет к краям, читается комфортно.
 *
 * Раньше ClaimCard/ContractCard были обёрнуты в <Modal> компонент,
 * который зажимал контент в 720px и не давал расположить блоки удобно.
 * Юрист жаловался: «листать что-либо неудобно». Перевели в страницы,
 * URL теперь содержит ID — back-button браузера работает, ссылки можно
 * шарить.
 */

const Shell = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding-bottom: ${({ theme }) => theme.spacing(8)};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(5)};
`;

const Title = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Subtitle = styled.div`
  margin-top: 2px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const TitleBlock = styled.div`
  flex: 1;
  min-width: 0;
`;

const Actions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-shrink: 0;
`;

interface Props {
  title: string;
  subtitle?: string;
  onBack: () => void;
  backLabel?: string;
  /** Слот для дополнительных кнопок в правой части шапки. */
  actions?: ReactNode;
  children: ReactNode;
}

export function CardShell({
  title,
  subtitle,
  onBack,
  backLabel = 'К списку',
  actions,
  children,
}: Props) {
  return (
    <Shell>
      <Header>
        <Button type="button" $variant="ghost" $size="sm" onClick={onBack}>
          <IconArrowLeft size={16} /> {backLabel}
        </Button>
        <TitleBlock>
          <Title>{title}</Title>
          {subtitle && <Subtitle>{subtitle}</Subtitle>}
        </TitleBlock>
        {actions && <Actions>{actions}</Actions>}
      </Header>
      {children}
    </Shell>
  );
}
